import { useEffect } from 'react';
import { Button, Center, Loader, Stack, Text, Title } from '@mantine/core';
import { useAuth } from '@workos-inc/authkit-react';
import { logger, useSignOut } from '~portal/lib';

/**
 * @desc How often to mint a fresh access token while the holding screen is open.
 *
 * Approval changes the user's WorkOS roles, and roles reach the SPA only inside a newly minted
 * access token — polling `users.me` would return the same held profile forever. A forced refresh
 * updates AuthKit's `permissions` state, the route guard re-evaluates, and the app appears without
 * the user signing out and back in (spec: "Approval detected from the holding screen").
 */
const REFRESH_INTERVAL_MS = 60_000;

/**
 * @desc The waiting-approval holding screen: an authenticated user who holds no access permission
 * for this deployment. Shown in place of the application, never alongside it.
 */
export function WaitingApproval() {
  const { getAccessToken } = useAuth();
  const signOut = useSignOut();

  useEffect(() => {
    const refresh = () => {
      getAccessToken({ forceRefresh: true }).catch((error: unknown) => {
        // Expected while the session is expiring or the network is down; AuthKit's own
        // `onRefreshFailure` drives the user back to sign-in when it is terminal.
        logger.debug('holding-screen token refresh failed', { error });
      });
    };

    const timer = window.setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [getAccessToken]);

  return (
    <Center h="100dvh" p="md" data-testid="waiting-approval">
      <Stack align="center" gap="sm" maw={440} ta="center">
        <Loader size="sm" mb="xs" />
        <Title order={3}>Your access is being reviewed</Title>
        <Text c="dimmed" size="sm">
          Your account is set up and waiting for an H2 administrator to approve it. This page
          updates by itself once you&rsquo;re approved &mdash; you don&rsquo;t need to sign in
          again.
        </Text>
        <Text c="dimmed" size="sm">
          Questions? Email <Text span>info@h2gc.com</Text>.
        </Text>
        <Button mt="xs" variant="default" onClick={signOut}>
          Sign out
        </Button>
      </Stack>
    </Center>
  );
}
